import { type FC, useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { BsFileEarmarkPlus, BsFolderPlus, BsArrowsExpand, BsArrowsCollapse } from 'react-icons/bs';
import { Button, Form } from 'react-bootstrap';

import { type RootState } from '@editor2/reducers/root';
import AddPageGroupModal from '@editor2/components/AddPageGroupModal';
import AddPageModal from '@editor2/components/AddPageModal';

import { editPageGroupsCollapseState, updateChapter } from '@editor2/reducers/editor/editorSlice';
import { CollapseState } from '@editor2/types/collapseState';
import PageGroupsList from './PageGroupsList';
import PagesList from './PagesList';
import { toolsBlock, contentTabStyle } from '@editor2/containers/ChapterPage/styles';

interface Props {
	chapterId: string;
	courseId: number;
}

const TableOfContents: FC<Props> = (props) => {
	const { chapterId, courseId } = props;

	const dispatch = useDispatch();

	const [isAddPageGroupModalShown, setAddPageGroupModalShown] = useState(false);
	const [isAddPageModalShown, setAddPageModalShown] = useState(false);

	const { chapters } = useSelector((state: RootState) => state.courses[courseId]);
	const chapter = chapters && chapters.entities[chapterId];

	const [editorialNotes, setEditorialNotes] = useState(chapter.notes);

	useEffect(() => {
		if (editorialNotes !== chapter.notes) setEditorialNotes(chapter.notes);
	}, [chapter.notes]);

	const onExpandAll = () => {
		dispatch(editPageGroupsCollapseState({ collapseState: CollapseState.ExpandAll, courseId }));
	};

	const onCollapseAll = () => {
		dispatch(editPageGroupsCollapseState({ collapseState: CollapseState.CollapseAll, courseId }));
	};

	const debouncedDispatch = useCallback(
		debounce((action) => {
			dispatch(action);
		}, 500),
		[]
	);

	const onChange = (value) => {
		setEditorialNotes(value.notes);
		debouncedDispatch(updateChapter({ chapterId: chapter.family_id, courseId, ...value }));
	};

	const tools = (
		<div className={toolsBlock}>
			{chapter && chapter.page_groups_enabled ? (
				<Button variant="outline-secondary" onClick={() => setAddPageGroupModalShown(true)}>
					<BsFolderPlus />
					Add page group
				</Button>
			) : (
				<Button variant="outline-secondary" onClick={() => setAddPageModalShown(true)}>
					<BsFileEarmarkPlus />
					Add page
				</Button>
			)}

			<Button variant="outline-secondary" onClick={onExpandAll}>
				<BsArrowsExpand />
				Expand All
			</Button>
			<Button variant="outline-secondary" onClick={onCollapseAll}>
				<BsArrowsCollapse />
				Collapse All
			</Button>
		</div>
	);

	return (
		<div className={contentTabStyle}>
			<Form.Group>
				<Form.Label>Editorial notes</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					value={editorialNotes}
					onChange={(e) => onChange({ notes: e.target.value })}
				/>
			</Form.Group>

			{tools}

			{chapter.page_groups_enabled ? (
				<PageGroupsList chapterId={chapterId} courseId={courseId} />
			) : (
				<PagesList chapterId={chapterId} courseId={courseId} />
			)}

			{isAddPageGroupModalShown && (
				<AddPageGroupModal
					chapterId={chapterId}
					index={0}
					onHide={() => setAddPageGroupModalShown(false)}
					isShown={isAddPageGroupModalShown}
					courseId={courseId}
				/>
			)}

			{isAddPageModalShown && (
				<AddPageModal
					chapterId={chapterId}
					index={0}
					onHide={() => setAddPageModalShown(false)}
					isShown={isAddPageModalShown}
					courseId={courseId}
				/>
			)}
		</div>
	);
};

export default TableOfContents;
